import React, { useEffect, useState, useMemo, useRef, forwardRef } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AdsClickOutlinedIcon from '@mui/icons-material/AdsClickOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import MenuIcon from '@mui/icons-material/Menu';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import UpgradeOutlinedIcon from '@mui/icons-material/UpgradeOutlined';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';

import SearchIcon from '../../assets/icons/search.png';
import RefreshIcon from '../../assets/icons/refresh.png';
import SettingsIcon from '../../assets/icons/settings.png';
import LoginIcon from '../../assets/icons/login.png';

import DefaultAppImage from '../../assets/images/logo.png';
import LogoWithTitle from '../../assets/images/logo_title.png';
import ScreamingFrogIcon from '../../assets/images/screaming_frog.png';

import Loader from 'components/Loader';
import SimpleBarScroll from 'components/SimpleBar';
import RequestToolModal from 'components/RequestToolModal';

import WhatsNewBanner from 'components/WhatsNewBanner';
import WhatsNewPage from 'components/WhatsNewPage';
import { WHATS_NEW_COLORS, collectNewAppIds, findWhatsNewApp } from 'utils/whatsNew';
import useSnackbar from 'hooks/useSnackbar';
import useMain from 'hooks/useMain';
import useAuth from 'hooks/useAuth';

const Tabs = {
  Applications: 1,
  Favorites: 2,
  Recents: 3,
  'Screaming Frog': 4,
  "What's New": 5
};

// The Screaming Frog entry is presented as the "Desktop Apps" section (Figma
// 58:2); the page itself keeps the vendor title.
const TAB_LABELS = {
  Applications: 'Tools',
  Favorites: 'Favorites',
  Recents: 'Recent',
  'Screaming Frog': 'Desktop Apps',
  "What's New": "What's New"
};

const CATEGORY_FILTERS = ['All', 'SEO', 'AI', 'Design', 'Advertising', 'Marketing', 'Automation', 'Productivity', 'Research'];

const CATEGORY_LABELS = {
  seo: 'SEO',
  ai: 'AI',
  design: 'Design',
  advertising: 'Advertising',
  marketing: 'Marketing',
  automation: 'Automation',
  productivity: 'Productivity',
  research: 'Research'
};


const SORT_ORDERS = ['none', 'az', 'za'];
const SF_TILE_GREEN = '#2FA84F';
const PROFILE_AVATAR_GRADIENT = 'linear-gradient(135deg, #5B6CF5 0%, #9A5CF6 100%)';

const listItemButtonSx = {
  borderRadius: '11px',
  py: 0.6,
  px: 1,
  border: '1px solid transparent',
  borderLeft: '3px solid transparent',
  '& .MuiListItemIcon-root': { color: '#aeb6c2' },
  '&.Mui-selected': {
    background: 'linear-gradient(180deg, #1e2c4a 0%, #161f39 100%)',
    borderColor: '#2c3a60',
    borderLeftColor: '#2b57e6',
    boxShadow: '0px 3px 12px 0px rgba(43, 87, 230, 0.16)',
    '& .MuiListItemIcon-root': { color: 'white' },
    '& .MuiTypography-root': { color: 'white', fontWeight: 600 }
  },
  '&.Mui-selected:hover': {
    background: 'linear-gradient(180deg, #1e2c4a 0%, #161f39 100%)'
  },
  '&:hover': { backgroundColor: '#121a2a' },
  my: 0.4
};

const listItemTextSx = {
  '& .MuiTypography-root': { fontSize: 15, fontWeight: 500, color: '#aeb6c2' }
};

const listItemIconSx = { minWidth: 32 };

const ADMIN_ASSET_BASE_URL = 'https://admin.kloow.com';
const APP_HEALTH_STATES = Object.freeze({
  CHECKING: 'checking',
  READY: 'ready',
  LOGIN_REQUIRED: 'login_required',
  SUBSCRIPTION_EXPIRED: 'subscription_expired',
  MAINTENANCE: 'maintenance'
});

const VALID_APP_HEALTH_STATES = new Set(Object.values(APP_HEALTH_STATES));

function createCheckingStatus() {
  return {
    state: APP_HEALTH_STATES.CHECKING,
    reason: 'pending_verification',
    serviceReachable: false,
    launchAllowed: false
  };
}

function normalizeStatusState(state) {
  if (typeof state !== 'string') return null;
  const normalized = state.toLowerCase();
  return VALID_APP_HEALTH_STATES.has(normalized) ? normalized : null;
}

function normalizeHealthStatus(rawStatus) {
  if (rawStatus && typeof rawStatus === 'object') {
    const normalizedState = normalizeStatusState(rawStatus.state);
    if (normalizedState) {
      return {
        state: normalizedState,
        reason:
          typeof rawStatus.reason === 'string' && rawStatus.reason.trim().length > 0 ? rawStatus.reason : null,
        serviceReachable: rawStatus.serviceReachable === true,
        launchAllowed: rawStatus.launchAllowed === true
      };
    }
  }

  if (rawStatus === true) {
    return { state: APP_HEALTH_STATES.READY, reason: 'legacy_ready', serviceReachable: true, launchAllowed: true };
  }

  if (rawStatus === false) {
    return { state: APP_HEALTH_STATES.MAINTENANCE, reason: 'legacy_failure', serviceReachable: false, launchAllowed: false };
  }

  return createCheckingStatus();
}

function getStatusPresentation(rawStatus) {
  const status = normalizeHealthStatus(rawStatus);

  switch (status.state) {
    case APP_HEALTH_STATES.READY:
      return { ...status, label: 'Available', helperText: 'Available to launch', buttonLabel: 'Launch', buttonDisabled: false, buttonColor: '#3a54c0' };
    case APP_HEALTH_STATES.LOGIN_REQUIRED:
      return { ...status, label: 'Update needed', helperText: 'An admin needs to refresh this session.', statusTooltip: 'An admin needs to refresh this session.', buttonLabel: 'Reconnect', buttonDisabled: false, buttonColor: '#2B5DB8' };
    case APP_HEALTH_STATES.SUBSCRIPTION_EXPIRED:
      return { ...status, label: 'Subscription expired', helperText: 'Unavailable', buttonLabel: 'Unavailable', buttonDisabled: true, buttonColor: '#245A78' };
    case APP_HEALTH_STATES.MAINTENANCE:
      return { ...status, label: 'Maintenance', helperText: 'Unavailable', buttonLabel: 'Unavailable', buttonDisabled: true, buttonColor: '#8D4955' };
    case APP_HEALTH_STATES.CHECKING:
    default:
      return { ...status, label: 'Checking...', helperText: 'Verifying access', buttonLabel: 'Checking...', buttonDisabled: true, buttonColor: '#3B4157' };
  }
}

function getButtonPresentation(statusInfo, isAdmin) {
  if (isAdmin) {
    if (statusInfo.state === APP_HEALTH_STATES.CHECKING) {
      return { label: 'Checking...', disabled: true, color: '#3B4157' };
    }
    if (statusInfo.launchAllowed) {
      return {
        label: statusInfo.state === APP_HEALTH_STATES.LOGIN_REQUIRED ? 'Reconnect' : 'Launch',
        disabled: false,
        color: statusInfo.buttonColor
      };
    }
    return { label: 'Unavailable', disabled: true, color: '#4D536B' };
  }

  if (statusInfo.state === APP_HEALTH_STATES.READY) {
    return { label: statusInfo.buttonLabel, disabled: statusInfo.buttonDisabled, color: statusInfo.buttonColor };
  }

  // Non-ready user states keep a greyed "Launch" (dark != disabled), per Figma.
  return { label: 'Launch', disabled: true, color: '#172033' };
}

function getPrimaryButtonIcon(statusInfo, isBusy, buttonPresentation) {
  if (isBusy) {
    return <AutorenewIcon sx={{ mr: 0.75, fontSize: 18, animation: 'spin 1.2s linear infinite' }} />;
  }
  if (statusInfo.state === APP_HEALTH_STATES.CHECKING && buttonPresentation.label === 'Checking...') {
    return <AutorenewIcon sx={{ mr: 0.75, fontSize: 18, animation: 'spin 1.2s linear infinite' }} />;
  }
  if (buttonPresentation.label === 'Launch') {
    return <PlayArrowIcon sx={{ mr: 0.5, fontSize: 18 }} />;
  }
  return null;
}

function statusColor(rawStatus) {
  const status = normalizeHealthStatus(rawStatus);
  if (status.state === APP_HEALTH_STATES.CHECKING) return '#A7B0CA';
  if (status.state === APP_HEALTH_STATES.READY) return '#5fcb8a';
  if (status.state === APP_HEALTH_STATES.LOGIN_REQUIRED) return '#b07cf0';
  if (status.state === APP_HEALTH_STATES.MAINTENANCE) return '#e5a93c';
  if (status.state === APP_HEALTH_STATES.SUBSCRIPTION_EXPIRED) return '#FFB25C';
  return '#FF7A7A';
}

function getAccountLabel(index) {
  return `Account ${index + 1}`;
}

function normalizeServerAddress(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  try {
    return new URL(rawValue).hostname || rawValue;
  } catch {
    try {
      return new URL(`http://${rawValue}`).hostname || rawValue;
    } catch {
      return rawValue.replace(/^\/+|\/+$/g, '');
    }
  }
}

function getServerIdentity(serverOption) {
  if (typeof serverOption === 'string') return serverOption.trim().toLowerCase();
  if (!serverOption || typeof serverOption !== 'object') return '';
  const candidates = [serverOption.id, serverOption.name, serverOption.host, serverOption.domain, serverOption.ip, serverOption.server, serverOption.url];
  return (
    candidates
      .map((candidate) => (typeof candidate === 'number' ? String(candidate) : String(candidate || '').trim()))
      .find((candidate) => candidate.length > 0)
      ?.toLowerCase() || ''
  );
}

function getServerAddress(serverOption) {
  if (typeof serverOption === 'string') return normalizeServerAddress(serverOption);
  if (!serverOption || typeof serverOption !== 'object') return '';
  const candidates = [serverOption.ip, serverOption.host, serverOption.server, serverOption.domain, serverOption.url, serverOption.name];
  return candidates.map((candidate) => normalizeServerAddress(candidate)).find((candidate) => candidate.length > 0) || '';
}

function isServerSelected(selectedServer, serverOption, fallbackLabel) {
  if (selectedServer === serverOption) return true;
  const selectedIdentity = getServerIdentity(selectedServer);
  const optionIdentity = getServerIdentity(serverOption);
  if (selectedIdentity && optionIdentity) return selectedIdentity === optionIdentity;
  if (typeof selectedServer === 'string') return selectedServer === fallbackLabel;
  return false;
}

function getSelectedAccountLabel(app, selectedServer) {
  if (!Array.isArray(app?.servers) || app.servers.length === 0) return 'Select account';
  const selectedIndex = app.servers.findIndex((serverOption, index) =>
    isServerSelected(selectedServer, serverOption, `${app.title} - ${index + 1}`)
  );
  return getAccountLabel(selectedIndex >= 0 ? selectedIndex : 0);
}

function createServerStatusRequestKey(appId, index) {
  return `${appId}::${index}`;
}

function getItemTitle(item) {
  return item?.title || item?.description || '';
}

function getItemImg(item) {
  return item?.logoPath ? `${ADMIN_ASSET_BASE_URL}/${item.logoPath}` : DefaultAppImage;
}

// Stable, hash-derived accent color for the letter-tile fallback (used by
// ToolLogo when a logo is missing or its asset fails to load). HSL hue from a
// hash of the tool id/title → each tool keeps the same color across renders.
function getToolFallbackColor(item) {
  const key = String(item?.id ?? getItemTitle(item) ?? '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 58%, 45%)`;
}

// Renders a tool's real logo (the same asset the cards use). Per the Figma SPECS
// "Tool icons & favorites" note: real logo always; the brand-color letter tile is
// only a RUNTIME fallback when there's no logo or the asset fails to load.
const ToolLogo = forwardRef(function ToolLogo({ app, size = 32, radius = 8, onClick, sx }, ref) {
  const [failed, setFailed] = useState(false);
  const title = getItemTitle(app);
  const hasLogo = Boolean(app?.logoPath);

  if (!hasLogo || failed) {
    return (
      <Box
        ref={ref}
        onClick={onClick}
        aria-label={title}
        sx={{
          width: size,
          height: size,
          borderRadius: `${radius}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: getToolFallbackColor(app),
          color: '#fff',
          fontWeight: 600,
          fontSize: Math.max(11, Math.round(size * 0.44)),
          lineHeight: 1,
          flexShrink: 0,
          userSelect: 'none',
          ...sx
        }}
      >
        {(title[0] || '?').toUpperCase()}
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      component="img"
      src={getItemImg(app)}
      alt={title}
      onClick={onClick}
      onError={() => setFailed(true)}
      sx={{
        width: size,
        height: size,
        borderRadius: `${radius}px`,
        objectFit: 'contain',
        backgroundColor: '#1E2233',
        flexShrink: 0,
        ...sx
      }}
    />
  );
});

function getInitials(rawName) {
  const value = String(rawName || '').trim();
  if (!value) return 'U';
  const localPart = value.includes('@') ? value.split('@')[0] : value;
  const segments = localPart.split(/[._\-\s]+/).filter(Boolean);
  const letters = segments.length >= 2 ? `${segments[0][0]}${segments[1][0]}` : localPart.slice(0, 2);
  return letters.toUpperCase();
}

function getPlanName(user) {
  if (user?.role === 'admin') {
    return 'Admin';
  }
  const levels = (Array.isArray(user?.membershipInfo) ? user.membershipInfo : [])
    .flatMap((entry) => (Array.isArray(entry?.membershipDetails) ? entry.membershipDetails : []))
    .filter((level) => level && typeof level.label === 'string' && level.label.trim() && level.label.trim() !== '-');
  if (levels.length === 0) return 'Member';
  const top = levels.reduce((best, level) => ((Number(level.price) || 0) > (Number(best.price) || 0) ? level : best), levels[0]);
  return top.label.trim();
}

function getSortLabel(sortOrder) {
  if (sortOrder === 'none') return 'Default';
  if (sortOrder === 'az') return 'A-Z';
  return 'Z-A';
}

function getSortMenuLabel(sortOrder) {
  if (sortOrder === 'none') return 'Default';
  if (sortOrder === 'az') return 'A to Z';
  return 'Z to A';
}

function ShortcutChip({ children }) {
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: '8px',
        backgroundColor: '#1a2336',
        color: '#aeb6c2',
        fontSize: 13,
        fontWeight: 500,
        lineHeight: '16px',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </Box>
  );
}

function StatChip({ icon, iconColor, iconBg, borderColor, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, px: { xs: 1, md: 1.5 }, height: { xs: 50, md: 64 }, borderRadius: '12px', backgroundColor: '#1b2233', border: `1px solid ${borderColor}`, flexShrink: 0, minWidth: 0 }}>
      <Box sx={{ width: { xs: 30, md: 34 }, height: { xs: 30, md: 34 }, borderRadius: '9px', backgroundColor: iconBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ lineHeight: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: { xs: 11, md: 12 }, fontWeight: 500, color: '#8b94a5', lineHeight: '16px' }}>{label}</Typography>
        <Typography sx={{ fontSize: { xs: 16, md: 19 }, fontWeight: 700, color: 'white', lineHeight: { xs: '20px', md: '24px' } }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function ViewToggle({ viewMode, setViewMode }) {
  const buttonSx = (active) => ({
    borderRadius: '6px',
    p: 0.5,
    color: active ? 'white' : '#aeb6c2',
    backgroundColor: active ? '#3a54c0' : 'transparent',
    '&:hover': { backgroundColor: active ? '#314aad' : 'rgba(255,255,255,0.06)' }
  });
  return (
    <Stack direction="row" spacing={0.25} sx={{ bgcolor: '#121a2a', borderRadius: '8px', border: '1px solid #222c3e', p: 0.25 }}>
      <Tooltip title="Grid view">
        <IconButton size="small" onClick={() => setViewMode('grid')} sx={buttonSx(viewMode === 'grid')}>
          <GridViewRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="List view">
        <IconButton size="small" onClick={() => setViewMode('list')} sx={buttonSx(viewMode === 'list')}>
          <ViewListRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function SidebarNav({ selectedTab, onSelect, onOpenWhatsNew, whatsNewUnreadCount }) {
  const hasUnread = Number(whatsNewUnreadCount) > 0;
  const isWhatsNewActive = selectedTab === Tabs["What's New"];

  return (
    <>
      <List sx={{ py: 0 }}>
        {['Applications', 'Favorites', 'Recents'].map((key) => (
          <ListItem disablePadding key={key}>
            <ListItemButton selected={selectedTab === Tabs[key]} onClick={() => onSelect(Tabs[key])} sx={listItemButtonSx}>
              <ListItemIcon sx={listItemIconSx}>
                {key === 'Applications' && <GridViewRoundedIcon />}
                {key === 'Favorites' && <FavoriteBorderIcon />}
                {key === 'Recents' && <ScheduleIcon />}
              </ListItemIcon>
              <ListItemText primary={TAB_LABELS[key]} sx={listItemTextSx} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 1.5, borderColor: '#1c2536' }} />
      <List sx={{ py: 0 }}>
        <ListItem disablePadding>
          <ListItemButton selected={selectedTab === Tabs['Screaming Frog']} onClick={() => onSelect(Tabs['Screaming Frog'])} sx={listItemButtonSx}>
            <ListItemIcon sx={listItemIconSx}>
              <DesktopWindowsOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={TAB_LABELS['Screaming Frog']} sx={listItemTextSx} />
          </ListItemButton>
        </ListItem>
      </List>

      {/* What's New sits at the bottom of the sidebar (Figma 408:3) and carries
          an unread count while updates are unseen. mt:auto absorbs the free
          space so the profile card below stays pinned beneath it. */}
      <List sx={{ py: 0, mt: 'auto' }}>
        <ListItem disablePadding>
          <ListItemButton selected={isWhatsNewActive} onClick={onOpenWhatsNew} sx={listItemButtonSx}>
            <ListItemIcon sx={listItemIconSx}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary={TAB_LABELS["What's New"]} sx={listItemTextSx} />
            {hasUnread ? (
              <Box
                sx={{
                  minWidth: 30,
                  height: 22,
                  px: 0.75,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '11px',
                  backgroundColor: WHATS_NEW_COLORS.unreadBadgeBg,
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 600
                }}
              >
                {whatsNewUnreadCount > 99 ? '99+' : whatsNewUnreadCount}
              </Box>
            ) : null}
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );
}

function SidebarProfile({ user, profileMenuAnchor, onOpenProfileMenu, onCloseProfileMenu, onOpenSettings, onLogout }) {
  const open = Boolean(profileMenuAnchor);
  const displayName = user?.username || 'Account';
  const planLabel = getPlanName(user);
  const initials = getInitials(displayName);

  // No mt:'auto' here — SidebarNav's What's New list already absorbs the free
  // space, and a second auto margin would split it, floating both.
  return (
    <Box sx={{ pt: 2 }}>
      <Box
        component="button"
        type="button"
        onClick={onOpenProfileMenu}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.25,
          py: 1,
          border: '1px solid #1e2839',
          borderRadius: '12px',
          background: '#0f1626',
          color: 'white',
          cursor: 'pointer',
          outline: 'none',
          textAlign: 'left',
          '&:hover': { background: '#131b2d' }
        }}
      >
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '50%', background: PROFILE_AVATAR_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'white', letterSpacing: '0.5px' }}>
            {initials}
          </Box>
          <Box sx={{ position: 'absolute', right: -1, bottom: -1, width: 12, height: 12, borderRadius: '50%', backgroundColor: '#3DD68C', border: '2px solid #0f1626' }} />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: '18px', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', transform: 'rotate(45deg)', background: PROFILE_AVATAR_GRADIENT, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, lineHeight: '16px', color: '#8b94a5' }}>{planLabel}</Typography>
          </Stack>
        </Box>
        {open ? <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.82)' }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.82)' }} />}
      </Box>

      <Menu
        id="profile-menu"
        anchorEl={profileMenuAnchor}
        open={open}
        onClose={onCloseProfileMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { bgcolor: '#252834', color: 'white', border: 'solid 1px #343847', borderRadius: '10px', mb: 1, width: 214 } } }}
      >
        <Typography sx={{ color: 'white', px: 2, py: 1 }}>Profile settings</Typography>
        <MenuItem
          onClick={() => {
            onOpenSettings();
            onCloseProfileMenu();
          }}
          sx={{ color: 'white', '&:hover': { backgroundColor: '#1976d2' } }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src={SettingsIcon} alt="settings" style={{ width: 24, height: 24 }} />
            <Typography variant="body2">Account Settings</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={onLogout} sx={{ color: 'white', '&:hover': { backgroundColor: '#1976d2' } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src={LoginIcon} alt="logout" style={{ width: 24, height: 24 }} />
            <Typography variant="body2">Log Out</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </Box>
  );
}

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { logout } = useAuth();
  const {
    getAppList,
    checkHealth,
    appList,
    runApp,
    stopApp,
    searchPattern,
    setSearchPattern,
    setFavorite,
    setLog,
    postToolRequest,
    getFeatureFlags,
    getWhatsNew,
    markWhatsNewSeen,
    dismissWhatsNewBanner
  } = useMain();

  const { errorMessage } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [runningStatus, setRunningStatus] = useState({});
  const [runningFlag, setRunningFlag] = useState(false);
  const [tryRunningStatus, setTryRunningStatus] = useState([]);
  const [sortOrder, setSortOrder] = useState('none');
  const [selectedTab, setSelectedTab] = useState(Tabs.Applications);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('All');
  // The "New" chip filters the Tools grid by recency, independently of category.
  const [showOnlyNew, setShowOnlyNew] = useState(false);

  const [whatsNew, setWhatsNew] = useState({ entries: [], unreadCount: 0, newAppIds: [], banner: null });
  const [whatsNewLoading, setWhatsNewLoading] = useState(true);
  const [whatsNewError, setWhatsNewError] = useState('');
  const [whatsNewFilter, setWhatsNewFilter] = useState('all');

  const [profileAnchor, setProfileAnchor] = useState(null);
  const [anchorElAdd, setAnchorElAdd] = useState(null);
  const addOpen = Boolean(anchorElAdd);
  const handleOpenAdd = (e) => setAnchorElAdd(e.currentTarget);
  const handleCloseAdd = () => setAnchorElAdd(null);

  const [anchorElSort, setAnchorElSort] = useState(null);
  const sortOpen = Boolean(anchorElSort);
  const handleOpenSort = (e) => setAnchorElSort(e.currentTarget);
  const handleCloseSort = () => setAnchorElSort(null);

  const [openSetting, setOpenSetting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [requestToolOpen, setRequestToolOpen] = useState(false);
  const [submittingToolRequest, setSubmittingToolRequest] = useState(false);
  // Remote feature flag — gates the Request Tool button. Defaults ON; the admin
  // panel can turn the experiment off without a redeploy.
  const [requestToolEnabled, setRequestToolEnabled] = useState(true);

  const handleSubmitToolRequest = async (payload) => {
    setSubmittingToolRequest(true);
    try {
      return await postToolRequest(payload);
    } finally {
      setSubmittingToolRequest(false);
    }
  };

  const user = JSON.parse(localStorage.getItem('user')) || {};
  const isAdmin = user?.role === 'admin';
  const getStatusForApp = (id) => normalizeHealthStatus(appHealth?.[id]);

  const [serverSelection, setServerSelection] = useState({});
  const [appHealth, setAppHealth] = useState({});
  const [serverStatusByApp, setServerStatusByApp] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAppId, setMenuAppId] = useState(null);

  const searchInputRef = useRef(null);

  useEffect(() => {
    let active = true;
    getFeatureFlags()
      .then((flags) => {
        if (active && typeof flags?.request_tool_enabled === 'boolean') {
          setRequestToolEnabled(flags.request_tool_enabled);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const updateHealth = async () => {
      try {
        const healthStatuses = await checkHealth(serverSelection);
        if (healthStatuses) {
          setAppHealth((prev) => ({ ...prev, ...healthStatuses }));
        }
      } catch (err) {
        // ignore
      }
    };
    updateHealth();
    const interval = setInterval(updateHealth, 600000);
    return () => clearInterval(interval);
  }, [serverSelection]);

  useEffect(() => {
    getAppList().then((list) => {
      const initialStatus = {};
      const initialServerSelection = {};
      const initialHealth = {};
      (Array.isArray(list) ? list : []).forEach((app) => {
        if (Array.isArray(app?.servers) && app.servers.length > 0) {
          initialServerSelection[app.id] = app.servers[0];
        }
        initialStatus[app.id] = 0;
        initialHealth[app.id] = createCheckingStatus();
      });
      setServerSelection(initialServerSelection);
      setRunningStatus(initialStatus);
      setAppHealth(initialHealth);
      setServerStatusByApp({});
      setLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const initialStatus = appList.reduce((acc, app) => {
      acc[app.id] = app.port;
      return acc;
    }, {});
    setRunningStatus(initialStatus);
  }, [appList]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow || '';
      document.body.style.overflow = prevBodyOverflow || '';
    };
  }, []);

  const loadWhatsNew = async () => {
    setWhatsNewLoading(true);
    const result = await getWhatsNew();

    if (result?.status) {
      const data = result.data || {};
      setWhatsNew({
        entries: Array.isArray(data.entries) ? data.entries : [],
        unreadCount: Number(data.unread_count) || 0,
        newAppIds: Array.isArray(data.new_app_ids) ? data.new_app_ids : [],
        banner: data.banner || null
      });
      setWhatsNewError('');
    } else {
      setWhatsNewError(result?.message || "Failed to load What's New.");
    }

    setWhatsNewLoading(false);
  };

  useEffect(() => {
    loadWhatsNew();
    // eslint-disable-next-line
  }, []);

  // Opening What's New marks the batch seen: the sidebar unread badge clears and
  // the Tools banner for that batch retires (Figma dev note 405:432 item 4).
  const openWhatsNew = (filter = 'all') => {
    setWhatsNewFilter(filter);
    setSelectedTab(Tabs["What's New"]);
    setWhatsNew((previous) => ({ ...previous, unreadCount: 0, banner: null }));
    markWhatsNewSeen().then(() => loadWhatsNew());
  };

  // x on the banner hides THAT batch permanently for this user; a later batch
  // shows a new banner (item 2).
  const dismissBanner = () => {
    const batch = whatsNew.banner?.batch;
    if (!batch) return;
    setWhatsNew((previous) => ({ ...previous, banner: null }));
    dismissWhatsNewBanner(batch);
  };

  const run = async (id, url, proxyServer) => {
    try {
      setRunningFlag(true);
      setTryRunningStatus((prev) => [...prev, id]);
      const result = await runApp(id, url, proxyServer);
      if (!result.status) {
        errorMessage(result.message);
      } else {
        setLog(id);
        setRunningStatus((prev) => ({ ...prev, [id]: result.message }));
      }
      setTryRunningStatus((prev) => prev.filter((e) => e !== id));
      setRunningFlag(false);
    } catch (error) {
      setRunningFlag(false);
      errorMessage(`Failed to run the executable for id ${id}: ${error.message}`);
    }
  };

  const stop = async (id) => {
    try {
      setTryRunningStatus((prev) => [...prev, id]);
      const result = await stopApp(id);
      if (!result.status) {
        errorMessage(result.message);
      } else {
        setRunningStatus((prev) => ({ ...prev, [id]: 0 }));
      }
      setTryRunningStatus((prev) => prev.filter((e) => e !== id));
    } catch (error) {
      errorMessage(`Failed to stop the browser for id ${id}: ${error.message}`);
    }
  };

  const refreshAll = async () => {
    // Pick up announcements published since the page loaded, so a new release
    // batch can surface its banner without a reload.
    loadWhatsNew();
    setLoading(true);
    await Promise.all(
      appList.map(async (app) => {
        if (runningStatus[app.id]) {
          await stop(app.id);
        }
      })
    );
    getAppList().then((list) => {
      const initialServerSelection = {};
      const initialHealth = {};
      const initialStatus = (Array.isArray(list) ? list : []).reduce((acc, app) => {
        if (Array.isArray(app?.servers) && app.servers.length > 0) {
          initialServerSelection[app.id] = app.servers[0];
        }
        acc[app.id] = 0;
        initialHealth[app.id] = createCheckingStatus();
        return acc;
      }, {});
      setServerSelection(initialServerSelection);
      setRunningStatus(initialStatus);
      setAppHealth(initialHealth);
      setServerStatusByApp({});
      setLoading(false);
    });
  };

  const closeServerMenu = () => {
    setMenuAnchor(null);
    setMenuAppId(null);
  };

  const refreshServerStatusesForApp = async (app) => {
    if (app?.id === null || typeof app?.id === 'undefined' || !Array.isArray(app?.servers) || app.servers.length === 0) {
      return;
    }
    setServerStatusByApp((prev) => ({
      ...prev,
      [app.id]: Object.fromEntries(app.servers.map((_, index) => [index, createCheckingStatus()]))
    }));
    const requestPayload = Object.fromEntries(app.servers.map((server, index) => [createServerStatusRequestKey(app.id, index), server]));
    const healthStatuses = await checkHealth(requestPayload);
    const fallbackReason = healthStatuses ? 'missing_health_status' : 'health_request_failed';
    setServerStatusByApp((prev) => ({
      ...prev,
      [app.id]: Object.fromEntries(
        app.servers.map((_, index) => {
          const requestKey = createServerStatusRequestKey(app.id, index);
          return [index, normalizeHealthStatus(healthStatuses?.[requestKey] ?? { state: APP_HEALTH_STATES.MAINTENANCE, reason: fallbackReason })];
        })
      )
    }));
  };

  const openServerMenu = (event, app) => {
    setMenuAnchor(event.currentTarget);
    setMenuAppId(app?.id ?? null);
    if (app?.id !== null && typeof app?.id !== 'undefined' && Array.isArray(app?.servers) && app.servers.length > 0) {
      void refreshServerStatusesForApp(app);
    }
  };

  const selectServerForApp = (app, server, index) => {
    const nextStatus = serverStatusByApp?.[app.id]?.[index] ?? createCheckingStatus();
    setServerSelection((prev) => ({ ...prev, [app.id]: server }));
    setAppHealth((prev) => ({ ...prev, [app.id]: nextStatus }));
    closeServerMenu();
  };

  const getSortedApps = () => {
    if (!Array.isArray(appList)) return [];
    if (selectedTab === Tabs.Recents) {
      return [...appList].filter((app) => !!app.lastAccessed).sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed));
    }
    if (sortOrder === 'none') return appList;
    const sorted = [...appList].sort((a, b) => {
      const at = (a.title || '').toLowerCase();
      const bt = (b.title || '').toLowerCase();
      if (at < bt) return -1;
      if (at > bt) return 1;
      return 0;
    });
    return sortOrder === 'az' ? sorted : sorted.reverse();
  };

  const pageTitle = useMemo(() => {
    switch (selectedTab) {
      case Tabs.Favorites:
        return 'Favorites';
      case Tabs.Recents:
        return 'Recently Used';
      case Tabs['Screaming Frog']:
        return 'Screaming Frog';
      case Tabs["What's New"]:
        return "What's New";
      default:
        return 'Tools';
    }
  }, [selectedTab]);

  const pageDescription = useMemo(() => {
    switch (selectedTab) {
      case Tabs.Favorites:
        return 'Your most-used applications for quick access.';
      case Tabs.Recents:
        return "Applications you've launched recently.";
      case Tabs['Screaming Frog']:
        return 'The Screaming Frog SEO Spider and Log File Analyser';
      case Tabs["What's New"]:
        return 'Stay up to date with the latest tools, features, and improvements in KLOOW.';
      default:
        return 'Pre-loaded, ready-to-use marketing tools for faster campaigns.';
    }
  }, [selectedTab]);

  const visibleApps = useMemo(() => {
    return getSortedApps()
      .filter((app) => {
        if (!searchPattern) return true;
        const pattern = searchPattern.toLowerCase();
        return (app.title || '').toLowerCase().includes(pattern) || (app.description || '').toLowerCase().includes(pattern);
      })
      .filter((app) => {
        if (selectedTab === Tabs.Recents) return !!app.lastAccessed;
        if (selectedTab === Tabs.Favorites) return app.isFavorite;
        return true;
      });
    // eslint-disable-next-line
  }, [appList, searchPattern, selectedTab, sortOrder]);

  const isApplicationsTab = selectedTab === Tabs.Applications;
  const isScreamingFrogTab = selectedTab === Tabs['Screaming Frog'];
  const isWhatsNewTab = selectedTab === Tabs["What's New"];

  // Ids of tools published as "new" in What's New and still inside their NEW
  // badge window. Start from what the server resolved, then add any entry that
  // only names its tool (linked_app_id is optional in the admin form).
  const newAppIdSet = useMemo(() => {
    const ids = collectNewAppIds(whatsNew.entries, appList);
    (whatsNew.newAppIds || []).forEach((id) => ids.add(String(id)));
    return ids;
  }, [whatsNew.entries, whatsNew.newAppIds, appList]);

  // Logos for the banner, resolved the same way so a batch whose entries have no
  // explicit linked tool still shows them.
  const bannerApps = useMemo(() => {
    const entryIds = whatsNew.banner?.entry_ids;
    if (!Array.isArray(entryIds) || entryIds.length === 0) return [];
    const entriesById = new Map((whatsNew.entries || []).map((entry) => [entry.id, entry]));
    return entryIds
      .map((id) => findWhatsNewApp(entriesById.get(id), appList))
      .filter(Boolean)
      .slice(0, 3);
  }, [whatsNew.banner, whatsNew.entries, appList]);

  const appsForGrid = useMemo(() => {
    if (!isApplicationsTab) return visibleApps;

    let result = visibleApps;

    if (selectedCategory !== 'All') {
      result = result.filter((app) => (app.category || '').toLowerCase() === selectedCategory.toLowerCase());
    }

    if (showOnlyNew) {
      result = result.filter((app) => newAppIdSet.has(String(app.id)));
    }

    return result;
  }, [visibleApps, isApplicationsTab, selectedCategory, showOnlyNew, newAppIdSet]);

  // Only count tools the user can actually see in the grid.
  const newToolCount = useMemo(() => visibleApps.filter((app) => newAppIdSet.has(String(app.id))).length, [visibleApps, newAppIdSet]);

  const isList = viewMode === 'list';

  const allApps = Array.isArray(appList) ? appList : [];
  const totalTools = allApps.length;
  const availableCount = allApps.filter((app) => getStatusPresentation(getStatusForApp(app.id)).state === APP_HEALTH_STATES.READY).length;
  const maintenanceCount = allApps.filter((app) => getStatusPresentation(getStatusForApp(app.id)).state === APP_HEALTH_STATES.MAINTENANCE).length;

  const showSort = isApplicationsTab || selectedTab === Tabs.Favorites;
  const showViewToggle = !isScreamingFrogTab && !isWhatsNewTab;

  const sortControl = (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography sx={{ color: '#aeb6c2', fontSize: 13, fontWeight: 500 }}>Sort by:</Typography>
      <Stack direction="row" alignItems="center" spacing={1} onClick={handleOpenSort} sx={{ cursor: 'pointer', height: 36, background: '#121a2a', borderRadius: '8px', px: 1.25, border: '1px solid #222c3e' }}>
        <Typography sx={{ color: '#aeb6c2', fontSize: 13, fontWeight: 500 }}>{getSortLabel(sortOrder)}</Typography>
        {sortOpen ? <ExpandLessIcon sx={{ fontSize: 18, color: 'white' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'white' }} />}
      </Stack>
      <Menu
        anchorEl={anchorElSort}
        open={sortOpen}
        onClose={handleCloseSort}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { bgcolor: '#252834', color: 'white', borderRadius: '8px', border: 'solid 1px #343847', mt: 1 } } }}
      >
        {SORT_ORDERS.map((order) => (
          <MenuItem
            key={order}
            onClick={() => {
              setSortOrder(order);
              handleCloseSort();
            }}
            sx={{ px: 1, width: 154, mx: 1, borderRadius: '6px', background: sortOrder === order ? '#3B4157!important' : 'inherit', '&:hover': { background: '#1976d2!important' } }}
          >
            <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
              <Typography variant="body2" color="white">
                {getSortMenuLabel(order)}
              </Typography>
              <Stack alignItems="center" justifyContent="center" sx={{ width: 20, height: 20, background: '#EFEAFB', borderRadius: '6px' }}>
                {sortOrder === order && <CheckIcon sx={{ fontSize: 16, color: 'black' }} />}
              </Stack>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );

  const toolbarControls = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
      {showSort ? sortControl : null}
      {showViewToggle ? <ViewToggle viewMode={viewMode} setViewMode={setViewMode} /> : null}
    </Stack>
  );

  const favoritesRow = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2.5 }}>
      <Typography sx={{ fontWeight: 500, color: '#aeb6c2', fontSize: 14 }}>Favorites</Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {appList
          ?.filter((app) => app?.isFavorite)
          .map((app, idx) => (
            <Tooltip key={`fav_${idx}`} title={getItemTitle(app)}>
              <ToolLogo
                app={app}
                size={32}
                radius={8}
                onClick={() => run(app.id, app.initUrl, app.servers?.[0])}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          ))}
        {appList?.filter((app) => !app?.isFavorite).length === 0 ? null : (
          <>
            <IconButton
              size="small"
              onClick={handleOpenAdd}
              sx={{ color: '#aeb6c2', borderRadius: '8px', backgroundColor: '#161e2e', border: '1px dashed #2a3344', '&:hover': { backgroundColor: '#1a2336' }, width: 32, height: 32 }}
            >
              {addOpen ? <CloseIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
            </IconButton>
            <Menu
              anchorEl={anchorElAdd}
              open={addOpen}
              onClose={handleCloseAdd}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { bgcolor: '#252834', color: 'white', borderRadius: '8px', border: 'solid 1px #343847', mt: 1, maxHeight: 420, overflowY: 'auto' } } }}
            >
              <MenuItem sx={{ pointerEvents: 'none' }}>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Add a favorite
                </Typography>
              </MenuItem>
              {appList
                ?.filter((app) => !app?.isFavorite)
                .map((app, idx) => (
                  <MenuItem
                    key={`add_${idx}`}
                    onClick={() => {
                      setFavorite(app?.id);
                      handleCloseAdd();
                    }}
                    sx={{ color: 'white', '&:hover': { backgroundColor: '#1976d2' } }}
                  >
                    <ToolLogo app={app} size={24} radius={6} sx={{ mr: 1 }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {getItemTitle(app)}
                    </Typography>
                  </MenuItem>
                ))}
            </Menu>
          </>
        )}
      </Stack>
    </Stack>
  );

  const renderAppCard = (app) => {
    const appStatus = getStatusForApp(app.id);
    const statusInfo = getStatusPresentation(appStatus);
    const buttonPresentation = getButtonPresentation(statusInfo, isAdmin);
    const isBusy = tryRunningStatus.includes(app.id);
    const hasMultipleServers = Array.isArray(app.servers) && app.servers.length > 1;
    const selectedServer = serverSelection[app.id] ?? app.servers?.[0];
    const selectedAccountLabel = getSelectedAccountLabel(app, selectedServer);
    const subtitle = CATEGORY_LABELS[app.category] || app.category || '';
    const isRunning = !!runningStatus[app.id];

    return (
      <Box key={`app_${app.id}`} sx={{ width: '100%', height: '100%', bgcolor: '#1b2233', color: 'white', borderRadius: '10px', border: '1px solid #242b3c', p: 2, display: 'flex', flexDirection: isList ? 'row' : 'column', alignItems: isList ? 'center' : 'stretch', gap: isList ? 2 : 1.25 }}>
          <Stack direction="row" alignItems={isList ? 'center' : 'flex-start'} spacing={1.25} sx={{ minWidth: 0, flexGrow: isList ? 1 : 0, width: isList ? 'auto' : '100%' }}>
            <ToolLogo app={app} size={44} radius={10} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Tooltip title={app.title} placement="top">
                  <Typography
                    component="h2"
                    sx={{ fontWeight: 600, fontSize: 16, lineHeight: '20px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                  >
                    {app.title}
                  </Typography>
                </Tooltip>
                {newAppIdSet.has(String(app.id)) ? (
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 0.875,
                      height: 22,
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: '11px',
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                      color: 'white',
                      backgroundColor: WHATS_NEW_COLORS.cardBadgeBg
                    }}
                  >
                    NEW
                  </Box>
                ) : null}
              </Stack>
              {subtitle ? (
                <Typography sx={{ display: 'block', color: '#7c8597', fontSize: 13, fontWeight: 400, lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              onClick={() => setFavorite(app?.id)}
              size="small"
              aria-label={app.isFavorite ? 'Remove favorite' : 'Add favorite'}
              sx={{ flexShrink: 0, p: 0.5, color: app.isFavorite ? '#FF6B6B' : 'rgba(255, 255, 255, 0.45)', '&:hover': { color: app.isFavorite ? '#FF6B6B' : 'rgba(255, 255, 255, 0.8)' } }}
            >
              {app.isFavorite ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Stack>

          {app.isAllowed ? (
            <Tooltip title={statusInfo.statusTooltip || ''} placement="top">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ...(isList ? { minWidth: 132, flexShrink: 0 } : {}) }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor(appStatus), flexShrink: 0 }} />
                <Typography sx={{ color: statusColor(appStatus), fontWeight: 600, fontSize: 13, lineHeight: '16px' }}>{statusInfo.label}</Typography>
              </Box>
            </Tooltip>
          ) : null}

          <Box sx={{ mt: isList ? 0 : 'auto', width: isList ? 320 : 'auto', maxWidth: '100%', flexShrink: 0 }}>
            {!app.isAllowed ? (
              <Button
                fullWidth
                disableElevation
                variant="contained"
                onClick={() => window.location.assign(`https://${app.domain}`)}
                disabled={runningFlag}
                sx={{ fontWeight: 700, textTransform: 'none', minHeight: 38, borderRadius: '9px', fontSize: 14, backgroundColor: '#c74ad3', '&:hover': { backgroundColor: '#b83fc4' } }}
              >
                <UpgradeOutlinedIcon sx={{ mr: 0.75, fontSize: 18 }} />
                Upgrade
              </Button>
            ) : isRunning ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                <Button
                  fullWidth
                  disableElevation
                  variant="contained"
                  onClick={() => stop(app.id)}
                  disabled={isBusy || runningFlag}
                  sx={{ fontWeight: 700, textTransform: 'none', minHeight: 38, borderRadius: '9px', fontSize: 14, backgroundColor: '#E03E3E', boxShadow: 'none', '&.Mui-disabled': { backgroundColor: '#7A4141', color: 'rgba(255,255,255,0.72)' }, '&:hover': { backgroundColor: '#c93636', boxShadow: 'none' } }}
                >
                  <PauseIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Stop
                </Button>
                <Button
                  fullWidth
                  disableElevation
                  variant="contained"
                  onClick={() => window.open(`https://www.kloow.com:${runningStatus[app.id]}`, '_blank')}
                  disabled={isBusy || runningFlag}
                  sx={{ fontWeight: 700, textTransform: 'none', minHeight: 38, borderRadius: '9px', fontSize: 14, backgroundColor: '#28A745', boxShadow: 'none', '&.Mui-disabled': { backgroundColor: '#266C39', color: 'rgba(255,255,255,0.72)' }, '&:hover': { backgroundColor: '#23913d', boxShadow: 'none' } }}
                >
                  <AdsClickOutlinedIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  View
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: hasMultipleServers ? 'minmax(0, 1.2fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 1 }}>
                <Button
                  fullWidth
                  disableElevation
                  variant="contained"
                  onClick={() => run(app.id, app.initUrl, selectedServer)}
                  disabled={isBusy || runningFlag || buttonPresentation.disabled}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    minHeight: 38,
                    borderRadius: '9px',
                    fontSize: 14,
                    background: buttonPresentation.color,
                    boxShadow: 'none',
                    minWidth: 0,
                    '&.Mui-disabled': { background: buttonPresentation.color, color: '#5b6678' },
                    '&:hover': { background: buttonPresentation.color, boxShadow: 'none' },
                    '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
                  }}
                >
                  {getPrimaryButtonIcon(statusInfo, isBusy, buttonPresentation)}
                  {isBusy ? (buttonPresentation.label === 'Reconnect' ? 'Reconnecting...' : 'Launching...') : buttonPresentation.label}
                </Button>
                {hasMultipleServers && (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={(event) => openServerMenu(event, app)}
                      disabled={isBusy || runningFlag}
                      endIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                      sx={{
                        color: '#aeb6c2',
                        textTransform: 'none',
                        justifyContent: 'space-between',
                        minHeight: 38,
                        px: 1.25,
                        borderRadius: '9px',
                        fontSize: 13,
                        fontWeight: 500,
                        minWidth: 0,
                        borderColor: '#222c3e',
                        background: '#121a2a',
                        '& .MuiButton-endIcon': { ml: 0.5, mr: 0 },
                        '&:hover': { borderColor: '#2a3556', background: '#161e2e' },
                        '&.Mui-disabled': { borderColor: '#222c3e', background: '#121a2a', color: '#5b6678' }
                      }}
                    >
                      <Typography component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit', fontWeight: 'inherit' }}>
                        {selectedAccountLabel}
                      </Typography>
                    </Button>
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor) && menuAppId === app.id}
                      onClose={closeServerMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      slotProps={{
                        list: { disablePadding: true, sx: { p: 1 } },
                        paper: {
                          sx: {
                            minWidth: Math.max(menuAnchor?.clientWidth || 0, 320),
                            bgcolor: '#1B1F35',
                            color: 'white',
                            borderRadius: '18px',
                            border: '1px solid rgba(82, 93, 152, 0.68)',
                            boxShadow: '0 22px 44px rgba(5, 9, 24, 0.48)',
                            mt: 1,
                            backgroundImage: 'linear-gradient(180deg, rgba(30, 36, 64, 0.98) 0%, rgba(16, 20, 38, 0.98) 100%)',
                            overflow: 'hidden'
                          }
                        }
                      }}
                    >
                      {(app.servers || []).map((srv, idx) => {
                        const label = getAccountLabel(idx);
                        const serverAddress = getServerAddress(srv);
                        const serverStatus = serverStatusByApp?.[app.id]?.[idx];
                        const serverStatusInfo = getStatusPresentation(serverStatus);
                        const serverStatusTone = statusColor(serverStatus);
                        const selected = isServerSelected(selectedServer, srv, `${app.title} - ${idx + 1}`);
                        return (
                          <MenuItem
                            key={`srv_${app.id}_${idx}`}
                            onClick={() => selectServerForApp(app, srv, idx)}
                            selected={selected}
                            sx={{
                              px: 1.4,
                              py: 1.35,
                              alignItems: 'stretch',
                              borderRadius: '14px',
                              border: selected ? '1px solid rgba(99, 123, 242, 0.56)' : '1px solid transparent',
                              backgroundColor: selected ? 'rgba(59, 79, 157, 0.28)' : 'transparent',
                              '&.Mui-selected': { backgroundColor: 'rgba(59, 79, 157, 0.28)' },
                              '&.Mui-selected:hover': { backgroundColor: 'rgba(67, 88, 174, 0.34)' },
                              '&:hover': { backgroundColor: 'rgba(46, 54, 92, 0.92)' },
                              '&:not(:last-of-type)': { mb: 0.5 }
                            }}
                          >
                            <Stack spacing={isAdmin && serverAddress ? 0.75 : 0.1} sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: serverStatusTone, boxShadow: `0 0 0 3px ${serverStatusTone}22`, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: serverStatusTone, fontWeight: 700, fontSize: 16, lineHeight: '22px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {`${label} - ${serverStatusInfo.label}`}
                                </Typography>
                              </Box>
                              {isAdmin && serverAddress && (
                                <Typography variant="caption" sx={{ pl: 3.3, color: 'rgba(214, 220, 247, 0.8)', fontSize: 12.5, lineHeight: '16px' }}>
                                  IP: {serverAddress}
                                </Typography>
                              )}
                            </Stack>
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
    );
  };

  const topBar = (
    // Wraps instead of clipping (matches kloow_app DashboardHeader). In the
    // ≥md desktop band the search shrinks first, then the stats+refresh+Request
    // cluster drops to a 2nd row — was flexWrap:'nowrap' inside an overflow:hidden
    // content Box, so the right-side items got cut off between ~900–1100px wide.
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: { xs: 1, md: 1.5 }, px: { xs: 2, md: 3 }, py: { xs: 1.25, md: 2 }, color: 'white' }}>
      {isMobile && (
        <IconButton onClick={() => setMobileMenuOpen(true)} sx={{ color: 'white', backgroundColor: '#121a2a', border: '1px solid #222c3e', borderRadius: '9px', width: 40, height: 40, flexShrink: 0 }}>
          {/* The sidebar (and its What's New unread badge) is hidden on mobile,
              so surface the count on the only visible entry point. */}
          <Badge
            badgeContent={whatsNew.unreadCount}
            invisible={!(Number(whatsNew.unreadCount) > 0)}
            sx={{ '& .MuiBadge-badge': { backgroundColor: WHATS_NEW_COLORS.unreadBadgeBg, color: 'white' } }}
          >
            <MenuIcon />
          </Badge>
        </IconButton>
      )}
      <OutlinedInput
        fullWidth
        size="small"
        inputRef={searchInputRef}
        value={searchPattern}
        onChange={(e) => setSearchPattern(e.target.value)}
        placeholder="Search tools, categories or accounts..."
        startAdornment={
          <InputAdornment position="start">
            <img src={SearchIcon} alt="search" style={{ width: 16, height: 16 }} />
          </InputAdornment>
        }
        endAdornment={
          isMobile ? null : (
            <InputAdornment position="end">
              <Stack direction="row" spacing={0.5}>
                <ShortcutChip>⌘ K</ShortcutChip>
                <ShortcutChip>Ctrl + K</ShortcutChip>
              </Stack>
            </InputAdornment>
          )
        }
        sx={{
          color: 'white',
          background: '#1b2233',
          borderRadius: '14px',
          height: '50px',
          flexGrow: 1,
          flexBasis: 280,
          minWidth: 200,
          fontSize: 15,
          '& .MuiInputBase-input::placeholder': { color: '#7a8499', opacity: 1 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#26314a' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#26314a' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3a54c0' }
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, flexShrink: 0, flexBasis: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
        <Box sx={{ display: 'flex', flexWrap: { xs: 'nowrap', md: 'wrap' }, alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%', overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 0.5, md: 0 }, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
          <StatChip icon={<GridViewRoundedIcon sx={{ fontSize: 16 }} />} iconColor="#7c9cff" iconBg="#1a2547" borderColor="#2a3556" label="Total Tools" value={totalTools} />
          <StatChip icon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />} iconColor="#5fcb8a" iconBg="#15301f" borderColor="#244235" label="Available" value={availableCount} />
          <StatChip icon={<BuildRoundedIcon sx={{ fontSize: 15 }} />} iconColor="#e5a93c" iconBg="#2e2611" borderColor="#3d3320" label="Maintenance" value={maintenanceCount} />
        </Box>

        <IconButton onClick={refreshAll} sx={{ width: 40, height: 40, borderRadius: '9px', backgroundColor: '#121a2a', border: '1px solid #222c3e', color: 'white', flexShrink: 0, '&:hover': { backgroundColor: '#1a2336' } }}>
          <Box component="img" src={RefreshIcon} alt="refresh" sx={{ width: 22, height: 22 }} />
        </IconButton>

        {requestToolEnabled && (
          <Button
            variant="contained"
            disableElevation
            onClick={() => setRequestToolOpen(true)}
            startIcon={<AddRoundedIcon />}
            sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600, fontSize: 14, height: 46, px: 2, borderRadius: '10px', backgroundColor: '#3a54c0', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#314aad' } }}
          >
            Request Tool
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 1, backgroundColor: '#0e1525' }} />

      <Stack direction="row" sx={{ position: 'relative', zIndex: 2, height: '100vh', width: '100%', overflow: 'hidden' }}>
        {!isMobile && (
          <Box sx={{ width: 262, flexShrink: 0, height: '100vh', p: 2.5, display: 'flex', flexDirection: 'column', backgroundColor: '#0c111e', borderRight: '1px solid #1a2233' }}>
            <Box sx={{ px: 1, py: 1.5, mb: 1 }}>
              <img src={LogoWithTitle} alt="KLOOW" style={{ height: 28 }} />
            </Box>
            <SidebarNav
              selectedTab={selectedTab}
              onSelect={setSelectedTab}
              onOpenWhatsNew={() => openWhatsNew('all')}
              whatsNewUnreadCount={whatsNew.unreadCount}
            />
            <SidebarProfile
              user={user}
              profileMenuAnchor={profileAnchor}
              onOpenProfileMenu={(e) => setProfileAnchor(e.currentTarget)}
              onCloseProfileMenu={() => setProfileAnchor(null)}
              onOpenSettings={() => setOpenSetting(true)}
              onLogout={logout}
            />
          </Box>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {topBar}

          {loading ? (
            <Loader />
          ) : (
            <Stack spacing={{ xs: 1.25, md: 2 }} sx={{ flexGrow: 1, minHeight: 0, width: '100%', pt: 0.5 }}>
              {isApplicationsTab ? (
                <>
                  <WhatsNewBanner
                    banner={whatsNew.banner}
                    apps={bannerApps}
                    onExplore={() => openWhatsNew('new_tool')}
                    onDismiss={dismissBanner}
                    LogoComponent={ToolLogo}
                  />
                  {favoritesRow}
                  <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ px: 2.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: { xs: 'nowrap', md: 'wrap' }, gap: 1, alignItems: 'center', flexGrow: 1, minWidth: 0, width: { xs: '100%', md: 'auto' }, overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 0.5, md: 0 }, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                      {CATEGORY_FILTERS.map((category) => {
                        const isAll = category === 'All';
                        const isSelected = selectedCategory === category;
                        return (
                          <Box
                            key={category}
                            component="button"
                            type="button"
                            onClick={() => setSelectedCategory(category)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              height: 34,
                              flexShrink: 0,
                              px: 1.75,
                              borderRadius: '8px',
                              fontSize: 13,
                              fontWeight: 500,
                              cursor: 'pointer',
                              userSelect: 'none',
                              outline: 'none',
                              color: isSelected ? 'white' : '#aeb6c2',
                              backgroundColor: isSelected ? '#3a54c0' : '#121a2a',
                              border: '1px solid',
                              borderColor: isSelected ? '#3a54c0' : '#222c3e',
                              '&:hover': { backgroundColor: isSelected ? '#3a54c0' : '#161e2e', color: 'white' }
                            }}
                          >
                            {isAll && <GridViewRoundedIcon sx={{ fontSize: 16 }} />}
                            {category}
                          </Box>
                        );
                      })}

                      {/* "New" sits apart from the category chips: it filters by
                          recency, not by category (Figma 405:422). */}
                      {newToolCount > 0 || showOnlyNew ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setShowOnlyNew((previous) => !previous)}
                          aria-pressed={showOnlyNew}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            height: 34,
                            flexShrink: 0,
                            px: 1.75,
                            borderRadius: '8px',
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                            userSelect: 'none',
                            outline: 'none',
                            color: 'white',
                            backgroundColor: showOnlyNew ? WHATS_NEW_COLORS.newChipCountBg : WHATS_NEW_COLORS.newChipBg,
                            border: '1px solid',
                            borderColor: WHATS_NEW_COLORS.newChipBorder,
                            transition: 'background-color 140ms ease'
                          }}
                        >
                          New
                          <Box
                            sx={{
                              minWidth: 20,
                              height: 20,
                              px: 0.5,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '10px',
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'white',
                              backgroundColor: showOnlyNew ? 'rgba(255,255,255,0.22)' : WHATS_NEW_COLORS.newChipCountBg
                            }}
                          >
                            {newToolCount}
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                    {toolbarControls}
                  </Stack>
                </>
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent="space-between" spacing={2} sx={{ px: 2.5, pt: 2 }}>
                  <Stack>
                    <Typography color="white" sx={{ fontWeight: 700, fontSize: 28, lineHeight: '34px' }}>
                      {pageTitle}
                    </Typography>
                    {pageDescription ? (
                      <Typography color="white" sx={{ fontSize: 14, lineHeight: '20px', opacity: 0.8 }}>
                        {pageDescription}
                      </Typography>
                    ) : null}
                  </Stack>
                  {isScreamingFrogTab ? null : toolbarControls}
                </Stack>
              )}

              <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
                <SimpleBarScroll sx={{ height: '100%', '& .simplebar-content': { display: 'flex', flexDirection: 'column' }, px: 2.5, pb: 2.5 }}>
                  {isWhatsNewTab ? (
                    <WhatsNewPage
                      entries={whatsNew.entries}
                      appList={appList}
                      loading={whatsNewLoading}
                      error={whatsNewError}
                      activeFilter={whatsNewFilter}
                      onChangeFilter={setWhatsNewFilter}
                      onLaunchApp={(app) => run(app.id, app.initUrl, app.servers?.[0])}
                      onOpenExternal={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                      LogoComponent={ToolLogo}
                    />
                  ) : isScreamingFrogTab ? (
                    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ minHeight: 240, textAlign: 'center', color: '#aeb6c2' }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '14px', backgroundColor: SF_TILE_GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={ScreamingFrogIcon} alt="Screaming Frog" style={{ width: 34, height: 34, objectFit: 'contain' }} />
                      </Box>
                      <Typography sx={{ color: 'white', fontWeight: 600, fontSize: 18 }}>Available in the desktop app</Typography>
                      <Typography sx={{ maxWidth: 460 }}>
                        Screaming Frog support is offered exclusively through our desktop application for the best possible experience.
                      </Typography>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: isList ? '1fr' : 'repeat(auto-fill, minmax(290px, 1fr))',
                        gap: '21px'
                      }}
                    >
                      {appsForGrid.map((app) => renderAppCard(app))}
                      {appsForGrid.length === 0 && (
                        <Typography color="white" sx={{ gridColumn: '1 / -1' }}>
                          Nothing to show
                        </Typography>
                      )}
                    </Box>
                  )}
                </SimpleBarScroll>
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>

      <Modal open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, bgcolor: 'rgba(0,0,0,0.6)' }}>
          <Box sx={{ width: '82%', maxWidth: 320, height: '100%', bgcolor: '#0c111e', p: 2.5, display: 'flex', flexDirection: 'column', borderRight: '1px solid #1a2233' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <img src={LogoWithTitle} alt="logo" style={{ height: 26 }} />
              <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <SidebarNav
              selectedTab={selectedTab}
              onSelect={(tab) => {
                setSelectedTab(tab);
                setMobileMenuOpen(false);
              }}
              onOpenWhatsNew={() => {
                openWhatsNew('all');
                setMobileMenuOpen(false);
              }}
              whatsNewUnreadCount={whatsNew.unreadCount}
            />
            <SidebarProfile
              user={user}
              profileMenuAnchor={profileAnchor}
              onOpenProfileMenu={(e) => setProfileAnchor(e.currentTarget)}
              onCloseProfileMenu={() => setProfileAnchor(null)}
              onOpenSettings={() => {
                setOpenSetting(true);
                setMobileMenuOpen(false);
              }}
              onLogout={logout}
            />
          </Box>
        </Box>
      </Modal>

      <Modal open={openSetting} onClose={() => setOpenSetting(false)} aria-labelledby="settings-title">
        <Box className="modal" sx={{ width: { xs: '92vw', sm: '80vw' }, maxWidth: 640, backgroundColor: '#16171E', border: 'solid 1px #343951', borderRadius: '8px', p: { xs: 2, sm: 3 } }}>
          <Stack spacing={3}>
            <Typography variant="h6" color="white">
              Account Settings
            </Typography>
            <Divider sx={{ borderColor: '#343951' }} />
            <Typography color="white" sx={{ fontSize: 16, lineHeight: '22px' }}>
              Overview
            </Typography>
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Typography variant="body2" color="white">
                  E-mail
                </Typography>
                <Box sx={{ borderRadius: '6px', backgroundColor: '#252731', border: 'solid 1px #343951', py: 1, px: 1, width: 'max-content', minWidth: { xs: 0, sm: '380px' }, maxWidth: '100%', wordBreak: 'break-word', color: 'white' }}>
                  {user.username || 'N/A'}
                </Box>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2" color="white">
                  Plan
                </Typography>
                <Box sx={{ borderRadius: '20px', backgroundColor: '#3a54c0', border: 'solid 1px #343951', py: 0.5, px: 2, width: 'max-content', color: 'white' }}>
                  {getPlanName(user)}
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <RequestToolModal
        open={requestToolOpen}
        onClose={() => setRequestToolOpen(false)}
        onSubmit={handleSubmitToolRequest}
        submitting={submittingToolRequest}
      />
    </>
  );
};

export default Dashboard;
